import { FetchResult, useMutation } from '@apollo/client';
import React, { FunctionComponent } from 'react';
import { FormattedMessage, WrappedComponentProps as InjectedIntlProps } from 'react-intl';

import { Button } from 'src/app/components/Button/Button';
import { EventTrack, pushEventTracking } from 'src/app/helpers/analytics';
import { withIntl } from 'src/app/helpers/locale';
import { getAddVideoToPlaylistOptions, createPlaylistMutationOptions } from 'src/app/helpers/playlist';
import { GRAPHQL_ADD_TO_PLAYLIST_MUTATION, GRAPHQL_CREATE_PLAYLIST_MUTATION } from 'src/app/models/playlist';
import { InjectedToastProps } from 'src/app/models/toast';

type MyInjectedProps = InjectedIntlProps & InjectedToastProps;

interface MyOwnProps {
  onCreate: VoidFunction;
  onCancel?: VoidFunction;
  autoAddVideoId?: string;
  pushEventTrack?: EventTrack;
}

interface MyCreatePlaylistResult {
  createPlaylist: {
    __typename: string;
    id: string;
    name:string;
  };
}

type MyProps = MyOwnProps & MyInjectedProps;

const PlaylistCreateComponent: FunctionComponent<MyProps> = (props: MyProps): JSX.Element | null => {
  let playlistNameEl: HTMLInputElement | null;
  const { intl: { formatMessage }, onCreate, onCancel, autoAddVideoId, pushEventTrack } = props;
  const [createNewPlaylist] = useMutation(GRAPHQL_CREATE_PLAYLIST_MUTATION);
  const [addVideoToPlaylist] = useMutation(GRAPHQL_ADD_TO_PLAYLIST_MUTATION);

  return (
    <>
      <div className="fs-16 fw-700 mb-8">
        <FormattedMessage id="label.playlist.create.title" />
      </div>
      {onCancel
        ? <div className="my-8">
          <input
            className="flex-1 b-1 w-100 px-8 py-6 fs-14 outline-0 bg-jetblack fg-white"
            placeholder={formatMessage({ id: 'label.playlist.create.placeholder' })}
            spellCheck={false}
            ref={(ref) => { playlistNameEl = ref; }}
          />
          <div className="d-flex my-4 fld-row-reverse">
            <Button className="button@md--format-34 tt-uppercase fs-12 ls-2 fg-white ml-8" modifier="primary" onClick={handleCreatePlaylist}>
              <FormattedMessage id="label.button.save"/>
            </Button>
            <Button className="button@md--format-34 tt-uppercase fs-12 ls-2 fg-white b-1 ml-8" modifier="black-dark" onClick={onCancel}>
              <FormattedMessage id="label.button.cancel"/>
            </Button>
          </div>
        </div>
        : <div className="d-flex">
          <input
            className="flex-1 b-0 w-100 px-8 py-6 fs-14 outline-0 bg-jetblack fg-white"
            placeholder={formatMessage({ id: 'label.playlist.create.placeholder' })}
            spellCheck={false}
            ref={(ref) => { playlistNameEl = ref; }}
          />
          <Button className="button@md--format-34 tt-uppercase fs-12 ls-2 fg-white" modifier="primary" onClick={handleCreatePlaylist}>
            <FormattedMessage id="label.button.save"/>
          </Button>
        </div>
      }
    </>
  );

  function handleCreatePlaylist(): void {
    if (playlistNameEl && playlistNameEl.value) {
      const name = playlistNameEl.value;
      handlePushEvent('add new playlist', name);
      createNewPlaylist(createPlaylistMutationOptions(name))
        .then((result: FetchResult<MyCreatePlaylistResult>) => {
          if (result.data && !result.errors) {
            props.showToast('toast.playlist.create', { playlistName: result.data.createPlaylist.name });
            handleAutoAddVideo(result.data.createPlaylist.id, result.data.createPlaylist.name);
          }
          if (result.errors) {
            throw new Error('exceed limit');
          }
        })
        .catch((e: any) => {
          if (e.message && e.message === 'exceed limit') {
            props.showToast('toast.playlist.create.failed.limit', { playlistName: name });
          } else {
            props.showToast('toast.playlist.create.failed', { playlistName: name });
          }
        })
        .finally(onCreate);
    }
  }

  function handleAutoAddVideo(playlistId: string, playlistName: string): void {
    if (autoAddVideoId) {
      handlePushEvent('add to existing playlist', playlistName, pushEventTrack?.custom);
      addVideoToPlaylist(getAddVideoToPlaylistOptions(autoAddVideoId, playlistId))
        .then(() => props.showToast('toast.add.playlist.video', { playlistName }))
        .catch(() => props.showToast('toast.add.playlist.video.failed', { playlistName }));
    }
  }

  function handlePushEvent(action: string, label?: string, custom?: {}): void {
    if (pushEventTrack) {
      pushEventTracking(
        pushEventTrack['eventDetails.category'],
        action,
        label,
        custom
      );
    }
  }
};

export const PlaylistCreate = withIntl()(PlaylistCreateComponent) as React.FunctionComponent<MyOwnProps & InjectedToastProps>;
